import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Badge, Button, Card, Input, Spinner } from "@/components";
import type { AdminSentencePuzzle } from "@/lib/admin-types";
import { GAME_TYPE_MULTIPLE_CHOICE } from "@/lib/admin-types";
import { apiJson } from "@/lib/api";
import { formatAdminDateTime } from "@/lib/format";
import {
  parsePracticeGameType,
  practiceGameTypeLabel,
  termSheetPracticeSearch,
  type PracticeGameType,
} from "@/lib/term-sheet-practice";

type DetailSearch = {
  from?: "practice";
  resource_key?: string;
  title?: string;
  kind?: "static" | "cloud";
  sheet_id?: number;
  game_type?: PracticeGameType;
};

export const Route = createFileRoute("/admin/sentence-puzzles/$id")({
  component: SentencePuzzleDetailPage,
  validateSearch: (raw: Record<string, unknown>): DetailSearch => ({
    from: raw.from === "practice" ? "practice" : undefined,
    resource_key:
      typeof raw.resource_key === "string" ? raw.resource_key : undefined,
    title: typeof raw.title === "string" ? raw.title : undefined,
    kind: raw.kind === "cloud" ? "cloud" : raw.kind === "static" ? "static" : undefined,
    sheet_id:
      typeof raw.sheet_id === "number"
        ? raw.sheet_id
        : typeof raw.sheet_id === "string" && raw.sheet_id.trim()
          ? Number(raw.sheet_id)
          : undefined,
    game_type: parsePracticeGameType(raw.game_type),
  }),
});

function SentencePuzzleDetailPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const puzzleId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const practiceReturn =
    search.from === "practice" && search.resource_key
      ? {
          resourceKey: search.resource_key,
          title: search.title || search.resource_key,
          kind: search.kind ?? "static",
          sheetId: search.sheet_id,
          gameType: search.game_type,
        }
      : null;
  const backLabel = practiceReturn?.gameType
    ? `← ${practiceGameTypeLabel(practiceReturn.gameType)}`
    : practiceReturn
      ? "← Practice"
      : "← All sentence puzzles";
  const goBack = () => {
    if (practiceReturn) {
      void navigate({
        to: "/admin/term-sheets/practice",
        search: termSheetPracticeSearch(practiceReturn),
      });
      return;
    }
    void navigate({ to: "/admin/sentence-puzzles" });
  };

  const detailQ = useQuery({
    queryKey: ["admin", "sentence-puzzle", puzzleId],
    queryFn: () =>
      apiJson<AdminSentencePuzzle>(`/v1/admin/sentence-puzzles/${puzzleId}`),
    enabled: Number.isFinite(puzzleId),
  });

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<AdminSentencePuzzle>(`/v1/admin/sentence-puzzles/${puzzleId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["admin", "sentence-puzzle", puzzleId],
      });
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () =>
      apiJson<AdminSentencePuzzle>(
        `/v1/admin/sentence-puzzles/${puzzleId}/regenerate`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["admin", "sentence-puzzle", puzzleId],
      });
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiJson<void>(`/v1/admin/sentence-puzzles/${puzzleId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
      goBack();
    },
  });

  if (detailQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">
          {(detailQ.error as Error)?.message ?? "Not found"}
        </p>
        <Button
          variant="secondary"
          onClick={goBack}
        >
          {backLabel}
        </Button>
      </div>
    );
  }

  return (
    <SentencePuzzleEditor
      puzzle={detailQ.data}
      saving={saveMutation.isPending}
      regenerating={regenerateMutation.isPending}
      deleting={deleteMutation.isPending}
      saveError={(saveMutation.error as Error | null)?.message ?? null}
      onSave={(body) => saveMutation.mutate(body)}
      onRegenerate={() => regenerateMutation.mutate()}
      onDelete={() => deleteMutation.mutate()}
      onBack={goBack}
      backLabel={backLabel}
    />
  );
}

type EditorProps = {
  puzzle: AdminSentencePuzzle;
  saving: boolean;
  regenerating: boolean;
  deleting: boolean;
  saveError: string | null;
  onSave: (body: Record<string, unknown>) => void;
  onRegenerate: () => void;
  onDelete: () => void;
  onBack: () => void;
  backLabel: string;
};

function SentencePuzzleEditor({
  puzzle,
  saving,
  regenerating,
  deleting,
  saveError,
  onSave,
  onRegenerate,
  onDelete,
  onBack,
  backLabel,
}: EditorProps) {
  const [promptEn, setPromptEn] = useState(puzzle.prompt_translations.en ?? "");
  const [sentenceEn, setSentenceEn] = useState(
    puzzle.prompt_translations.en_sentence ?? "",
  );
  const [targetHmong, setTargetHmong] = useState(puzzle.target_hmong);
  const [tilesText, setTilesText] = useState(puzzle.tiles.join(" | "));
  const [distractorsText, setDistractorsText] = useState(
    puzzle.distractors.join(" | "),
  );
  const [suffix, setSuffix] = useState(puzzle.punctuation_suffix);
  const [resourceKey, setResourceKey] = useState(puzzle.resource_key ?? "");
  const [gameType, setGameType] = useState(puzzle.game_type ?? "");

  useEffect(() => {
    setPromptEn(puzzle.prompt_translations.en ?? "");
    setSentenceEn(puzzle.prompt_translations.en_sentence ?? "");
    setTargetHmong(puzzle.target_hmong);
    setTilesText(puzzle.tiles.join(" | "));
    setDistractorsText(puzzle.distractors.join(" | "));
    setSuffix(puzzle.punctuation_suffix);
    setResourceKey(puzzle.resource_key ?? "");
    setGameType(puzzle.game_type ?? "");
  }, [puzzle]);

  const parsePipeList = (value: string) =>
    value
      .split("|")
      .map((part) => part.trim())
      .filter(Boolean);

  const isMultipleChoice =
    (gameType || puzzle.game_type) === GAME_TYPE_MULTIPLE_CHOICE;

  const saveBody = (extra: Record<string, unknown> = {}) => {
    const trimmedTarget = targetHmong.trim();
    const promptTranslations: Record<string, string> = {
      ...puzzle.prompt_translations,
      en: promptEn,
    };
    if (isMultipleChoice) {
      const trimmedSentence = sentenceEn.trim();
      if (trimmedSentence) {
        promptTranslations.en_sentence = trimmedSentence;
      } else {
        delete promptTranslations.en_sentence;
      }
    }
    return {
      prompt_translations: promptTranslations,
      target_hmong: trimmedTarget,
      tiles: isMultipleChoice ? [trimmedTarget] : parsePipeList(tilesText),
      distractors: isMultipleChoice ? [] : parsePipeList(distractorsText),
      punctuation_suffix: isMultipleChoice ? "" : suffix,
      resource_key: resourceKey,
      game_type: resourceKey.trim()
        ? gameType.trim() || "build_sentence"
        : gameType,
      ...extra,
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button variant="secondary" onClick={onBack}>
            {backLabel}
          </Button>
          <h1 className="mt-3 text-2xl font-semibold">
            {puzzle.prompt_translations.en || puzzle.slug}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {puzzle.slug} · Generated{" "}
            {formatAdminDateTime(puzzle.generated_at)}
            {puzzle.generated_with_model
              ? ` · ${puzzle.generated_with_model}`
              : ""}
          </p>
        </div>
        <Badge variant={puzzle.is_published ? "success" : "neutral"}>
          {puzzle.is_published ? `Live #${puzzle.queue_order}` : "Draft"}
        </Badge>
      </div>

      <Card className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">
            {isMultipleChoice ? "Question" : "English prompt"}
          </label>
          <Input
            className="mt-1"
            value={promptEn}
            onChange={(e) => setPromptEn(e.target.value)}
          />
        </div>
        {isMultipleChoice ? (
          <div>
            <label className="text-sm font-medium text-gray-700">
              English translation
            </label>
            <Input
              className="mt-1"
              value={sentenceEn}
              onChange={(e) => setSentenceEn(e.target.value)}
              placeholder="Shown under Correct / Incorrect in the app"
            />
          </div>
        ) : null}
        <div>
          <label className="text-sm font-medium text-gray-700">
            {isMultipleChoice ? "Correct Hmong answer" : "Target Hmong"}
          </label>
          <Input
            className="mt-1"
            value={targetHmong}
            onChange={(e) => setTargetHmong(e.target.value)}
          />
        </div>
        {!isMultipleChoice ? (
          <>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Tiles (correct order, separated by |)
              </label>
              <Input
                className="mt-1"
                value={tilesText}
                onChange={(e) => setTilesText(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Distractors (separated by |)
              </label>
              <Input
                className="mt-1"
                value={distractorsText}
                onChange={(e) => setDistractorsText(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Punctuation suffix
              </label>
              <Input
                className="mt-1 max-w-[8rem]"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
              />
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-600">
            Wrong options are other published answers on this sheet, picked at
            play time. You only need the question and the correct answer.
          </p>
        )}
        <div>
          <label className="text-sm font-medium text-gray-700">
            Resource key (term sheet practice)
          </label>
          <Input
            className="mt-1"
            value={resourceKey}
            onChange={(e) => setResourceKey(e.target.value)}
            placeholder="/(cheatsheets)/asking-questions"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Game type</label>
          <Input
            className="mt-1 max-w-[16rem]"
            value={gameType}
            onChange={(e) => setGameType(e.target.value)}
            placeholder="build_sentence"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            disabled={saving}
            onClick={() => onSave(saveBody())}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          {!puzzle.is_published ? (
            <Button
              disabled={saving}
              onClick={() => onSave(saveBody({ is_published: true }))}
            >
              Approve & publish
            </Button>
          ) : (
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => onSave({ is_published: false })}
            >
              Unpublish
            </Button>
          )}
          {!isMultipleChoice ? (
            <Button
              variant="secondary"
              disabled={regenerating}
              onClick={onRegenerate}
            >
              {regenerating ? "Regenerating…" : "Regenerate"}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            disabled={deleting}
            onClick={() => {
              if (window.confirm("Delete this sentence puzzle?")) onDelete();
            }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </div>
        {saveError ? <p className="text-sm text-red-600">{saveError}</p> : null}
      </Card>

      {!isMultipleChoice ? (
        <Card>
          <h2 className="text-sm font-medium text-gray-700">Preview bank</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {[...parsePipeList(tilesText), ...parsePipeList(distractorsText)].map(
              (tile, idx) => (
                <span
                  key={`${tile}-${idx}`}
                  className="rounded-xl bg-[#C4EBC0] px-3 py-2 text-sm font-semibold text-gray-900"
                >
                  {tile}
                </span>
              ),
            )}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
